import React from "react";
import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { StoryAPI } from "../lib/api";
import StoryGrid from "../components/stories/StoryGrid";
import SearchAndFilter from "../components/stories/SearchAndFilter";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  Award,
  ImageOff,
  AlertCircle,
  ShoppingCart,
  Check,
} from "lucide-react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { cartService } from "../services/cartService";

interface Story {
  _id: string;
  title: string;
  long_description: string;
  type: string;
  context: string;
  city: string;
  region: string;
  is_featured: boolean;
  is_sponsored: boolean;
  is_available?: boolean;
  sponsor_id?: {
    name: string;
  };
  downloads: number;
  date_added: string;
  cover_image?: string;
  thumbnail_image?: string;
  product_id?: string;
  north_america_id?: string;
  international_id?: string;
}

interface StoriesResponse {
  stories: Story[];
  total: number;
  pages: number;
  currentPage: number;
}

const StoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [showAddedNotification, setShowAddedNotification] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Hard copy modal state
  const [showHardCopyModal, setShowHardCopyModal] = useState(false);
  const [hardCopyType, setHardCopyType] = useState<
    "north_america" | "international" | null
  >(null);
  const [isSubmittingHardCopy, setIsSubmittingHardCopy] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    phone: "",
  });

  // Fetch story details
  const {
    data: story,
    isLoading,
    error,
  } = useQuery<Story>({
    queryKey: ["story", id],
    queryFn: async () => {
      if (!id) throw new Error("No story ID provided");
      return StoryAPI.getById(id);
    },
    enabled: !!id,
  });

  // Fetch related stories
  const { data: relatedStories } = useQuery<StoriesResponse>({
    queryKey: ["related-stories", story?.type],
    queryFn: () =>
      StoryAPI.getAll({
        limit: 4,
        type: story?.type,
        exclude: story?._id,
      }),
    enabled: !!story?.type,
    placeholderData: { stories: [], total: 0, pages: 0, currentPage: 1 },
  });

  // Construct the full image path
  const getImagePath = (
    filename?: string,
    type: "covers" | "thumbnails" = "covers"
  ) => {
    if (!filename) return "";
    const cleanFilename = filename.replace(
      new RegExp(`^\\/?(?:images\\/stories\\/${type}\\/)?`),
      ""
    );
    return `/images/stories/${type}/${cleanFilename}`;
  };

  // Debug function to show the current cart state
  const debugCartState = () => {
    console.log("===== STORYDETAIL CART DEBUG =====");
    if (!story) return;
    console.log("Story:", story.title, "Product ID:", story.product_id);

    if (window.fastspring?.builder?.cart) {
      console.log(
        "FastSpring cart items:",
        window.fastspring.builder.cart.items
      );
      console.log(
        "FastSpring cart count:",
        window.fastspring.builder.cart.items?.length || 0
      );
    } else {
      console.log("FastSpring cart not accessible");
    }
    console.log("======================");
  };

  // Use direct cart refresh with better error handling and retry
  const refreshCart = useCallback(() => {
    if (!story) return -1;

    // Debug the current state
    debugCartState();

    // Try to access FastSpring directly
    if (window.fastspring?.builder?.cart) {
      const fsItems = window.fastspring.builder.cart.items || [];
      const fsCount = fsItems.length;

      console.log(`Story detail direct FastSpring check: ${fsCount} items`);

      // Update localStorage directly
      const fsProducts = fsItems.map((item) => item.product);
      const newLocalItems = fsProducts.map((productId) => ({
        productId,
        quantity: 1,
      }));

      localStorage.setItem(
        "fastspring_cart_items",
        JSON.stringify(newLocalItems)
      );
      return fsCount;
    }

    // Use the service as fallback
    try {
      cartService.addToCart(story.product_id || "", {
        title: story.title,
        imageUrl: getImagePath(story.cover_image),
      });
      return 1;
    } catch (error) {
      console.error("Error refreshing cart:", error);
      return -1;
    }
  }, [story]);

  // Handle FastSpring add to cart with improved feedback and error handling
  const handleAddToCart = async () => {
    if (!story || isAddingToCart) return;

    setIsAddingToCart(true);
    console.log(`Adding to cart: ${story.title} (${story.product_id})`);

    try {
      if (!story.product_id) {
        throw new Error("Missing product ID");
      }

      // Try to add directly to FastSpring
      if (window.fastspring?.builder) {
        window.fastspring.builder.add(story.product_id);
        console.log(`Directly added ${story.product_id} to FastSpring cart`);

        // Check if it was actually added
        setTimeout(() => {
          if (window.fastspring?.builder?.cart?.items) {
            const fsItems = window.fastspring.builder.cart.items;
            console.log("FastSpring items after direct add:", fsItems);
            const isInCart = fsItems.some(
              (item) => item.product === story.product_id
            );
            console.log(`Is product in FastSpring cart? ${isInCart}`);
          }
        }, 100);
      }

      const addedItem = cartService.addToCart(story.product_id, {
        title: story.title,
        imageUrl: getImagePath(story.cover_image),
      });

      if (addedItem) {
        console.log(`Successfully added ${story.title} to cart`);
        setShowAddedNotification(true);

        // Refresh cart with a delay to allow FastSpring to process
        setTimeout(() => {
          refreshCart();
        }, 300);

        // Hide notification after 3 seconds
        setTimeout(() => {
          setShowAddedNotification(false);
        }, 3000);
      } else {
        console.error(`Failed to add ${story.title} to cart`);
        alert("Failed to add item to cart. Please try again.");
      }
    } catch (error) {
      console.error(`Error adding ${story.title} to cart:`, error);
      alert("There was an error adding the item to your cart.");
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Handle hard copy purchase
  const handleHardCopyPurchase = async () => {
    if (!story || !hardCopyType || isSubmittingHardCopy) return;

    setIsSubmittingHardCopy(true);

    try {
      const productId =
        hardCopyType === "north_america"
          ? story.north_america_id
          : story.international_id;
      const price = hardCopyType === "north_america" ? "$34.95" : "$49.95";
      const region =
        hardCopyType === "north_america" ? "North America" : "International";

      if (!productId) {
        throw new Error(`No product ID available for ${region} shipping`);
      }

      // Add to cart
      cartService.addToCart(productId, {
        title: `${story.title} - Hard Copy (${region})`,
        imageUrl: getImagePath(story.cover_image),
      });

      // Send address information via FormSpree
      await fetch("https://formspree.io/f/xldearlz", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: addressForm.email,
          subject: `Hard Copy Order - ${story.title}`,
          story_title: story.title,
          story_id: story._id,
          shipping_type: region,
          price: price,
          customer_name: addressForm.name,
          customer_email: addressForm.email,
          customer_address: addressForm.address,
          customer_city: addressForm.city,
          customer_state: addressForm.state,
          customer_zip: addressForm.zipCode,
          customer_country: addressForm.country,
          customer_phone: addressForm.phone,
          _subject: `Hard Copy Order - ${story.title} (${region})`,
        }),
      });

      // Close modal and show success
      setShowHardCopyModal(false);
      setHardCopyType(null);
      setAddressForm({
        name: "",
        email: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
        phone: "",
      });

      // Show success notification
      setShowAddedNotification(true);
      setTimeout(() => {
        setShowAddedNotification(false);
      }, 3000);
    } catch (error) {
      console.error("Error processing hard copy order:", error);
      alert("There was an error processing your order. Please try again.");
    } finally {
      setIsSubmittingHardCopy(false);
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 animate-pulse">
        <div className="aspect-video bg-muted rounded-lg mb-8" />
        <div className="space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-full" />
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !story) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <h1 className="text-2xl font-semibold">Story Not Found</h1>
          <p className="text-muted-foreground">
            We couldn't find the story you're looking for. It may have been
            removed or the link might be incorrect.
          </p>
          <button
            onClick={() => navigate("/stories")}
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Stories
          </button>
        </div>
      </div>
    );
  }

  // Determine if the story is available (defaults to true if not specified)
  const isAvailable = story?.is_available !== false;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Back Button */}
      <Link
        to="/stories"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Stories
      </Link>

      {/* Hero Section */}
      <div className="relative aspect-video rounded-lg overflow-hidden mb-8">
        {!imageError ? (
          <img
            src={getImagePath(story.cover_image)}
            alt={story.title}
            className={`w-full h-full object-cover transition-opacity duration-200 ${
              imageLoading ? "opacity-0" : "opacity-100"
            }`}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <ImageOff className="h-16 w-16 text-muted-foreground/50" />
          </div>
        )}
        {imageLoading && !imageError && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8">
          <h1 className="text-4xl font-bold text-white mb-2 font-heading spotlight-text">{story.title}</h1>
          <p className="text-xl text-white/80">
            {story.city}, {story.region}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Story Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tags */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium bg-crimson/10 text-crimson px-3 py-1 rounded-sm">
              {story.type}
            </span>
            <span className="text-sm font-medium bg-gold/10 text-gold px-3 py-1 rounded-sm">
              {story.context}
            </span>
          </div>

          {/* Description */}
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-lg">{story.long_description}</p>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-muted-foreground">
            {isAvailable && (
              <>
                <div className="flex items-center gap-1">
                  <Download className="h-4 w-4" />
                  <span>{story.downloads} downloads</span>
                </div>
                <div className="h-4 w-px bg-border" />
              </>
            )}
            <span>Added {new Date(story.date_added).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Purchase Card */}
          <div className="bg-card rounded-sm border border-border/50 border-l-4 border-l-crimson p-6 space-y-6 relative">
            <div className="text-center">
              <div className="text-3xl font-bold font-heading text-gold">$14.95</div>
              <p className="text-muted-foreground">One-time purchase</p>
            </div>

            {/* Sponsorship Status - Added back inside purchase card */}
            {story.is_sponsored ? (
              <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
                <Award className="h-5 w-5 text-primary" />
                <p className="text-sm">
                  This story is sponsored{" "}
                  {story.sponsor_id?.name && (
                    <>
                      by{" "}
                      <span className="font-medium">
                        {story.sponsor_id.name}
                      </span>
                    </>
                  )}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
                <Award className="h-5 w-5" />
                <p className="text-sm">
                  This story is available for sponsorship. Sponsors earn credits
                  for future downloads by others!
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleAddToCart}
                disabled={!isAvailable || isAddingToCart}
              >
                <ShoppingCart className="h-4 w-4" />
                {!isAvailable
                  ? "Coming Soon!"
                  : isAddingToCart
                    ? "Adding..."
                    : "Add to Cart"}
              </button>

              {/* Hard Copy Button */}
              <button
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 px-4 py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowHardCopyModal(true)}
                disabled={!isAvailable}
              >
                <ShoppingCart className="h-4 w-4" />
                Order Hard Copy
              </button>

              {isSignedIn && (
                <>
                  {/* Future feature: credits purchase flow */}
                  {/* <button
                    className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!isSignedIn || !isAvailable}
                  >
                    Use 10 Credits
                  </button> */}
                  {!story?.is_sponsored && (
                    <button
                      className="w-full bg-accent text-accent-foreground hover:bg-accent/90 px-4 py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!isAvailable}
                    >
                      <Award className="h-4 w-4" />
                      Sponsor ($19.95) - Earn 5 Credits
                    </button>
                  )}
                </>
              )}
            </div>

            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Instant digital download
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                High-resolution game materials
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Print & digital formats included
              </li>
            </ul>

            {/* Added to Cart Notification */}
            {showAddedNotification && (
              <div className="cart-notification absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg z-[60] flex items-center gap-2">
                <Check className="h-5 w-5" />
                <span>Added to cart!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Stories */}
      {relatedStories && relatedStories.stories.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl font-semibold mb-6 font-heading">Similar Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedStories.stories.map((relatedStory) => (
              <Link
                key={relatedStory._id}
                to={`/stories/${relatedStory._id}`}
                className="group bg-card rounded-sm border border-border/50 border-l-4 border-l-crimson overflow-hidden hover:shadow-lg hover:shadow-crimson/10 transition-shadow"
              >
                <div className="aspect-video bg-muted relative">
                  <img
                    src={getImagePath(
                      relatedStory.thumbnail_image,
                      "thumbnails"
                    )}
                    alt={relatedStory.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-lg font-semibold text-white font-heading">
                      {relatedStory.title}
                    </h3>
                    <p className="text-sm text-white/80">
                      {relatedStory.city}, {relatedStory.region}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Hard Copy Modal */}
      {showHardCopyModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-card border rounded-lg shadow-lg max-w-md w-full p-6 my-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 sticky top-0 bg-card py-2">
              Order Hard Copy
            </h2>

            <div className="space-y-6">
              {/* Story Info */}
              <div className="bg-muted p-4 rounded-md">
                <h3 className="font-semibold mb-2">{story.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {story.city}, {story.region}
                </p>
              </div>

              {/* Shipping Options */}
              {!hardCopyType ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Select Shipping Option
                  </h3>

                  <button
                    onClick={() => setHardCopyType("north_america")}
                    className="w-full p-4 border rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">North America</h4>
                        <p className="text-sm text-muted-foreground">
                          USA, Canada, Mexico
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          $34.95
                        </div>
                        <div className="text-sm text-muted-foreground">USD</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setHardCopyType("international")}
                    className="w-full p-4 border rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">International</h4>
                        <p className="text-sm text-muted-foreground">
                          Worldwide shipping
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          $49.95
                        </div>
                        <div className="text-sm text-muted-foreground">USD</div>
                      </div>
                    </div>
                  </button>
                </div>
              ) : (
                /* Address Form */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Shipping Address</h3>
                    <button
                      onClick={() => setHardCopyType(null)}
                      className="text-sm text-primary hover:underline"
                    >
                      Change option
                    </button>
                  </div>

                  <div className="bg-muted p-4 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">
                        {hardCopyType === "north_america"
                          ? "North America"
                          : "International"}
                      </span>
                      <span className="text-lg font-bold text-primary">
                        {hardCopyType === "north_america" ? "$34.95" : "$49.95"}
                      </span>
                    </div>
                  </div>

                  <form
                    className="space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleHardCopyPurchase();
                    }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Full Name <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={addressForm.name}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Email <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={addressForm.email}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Street Address{" "}
                        <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={addressForm.address}
                        onChange={(e) =>
                          setAddressForm((prev) => ({
                            ...prev,
                            address: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          City <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={addressForm.city}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          State/Province{" "}
                          <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={addressForm.state}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              state: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          ZIP/Postal Code{" "}
                          <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={addressForm.zipCode}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              zipCode: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Country <span className="text-destructive">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={addressForm.country}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              country: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={addressForm.phone}
                          onChange={(e) =>
                            setAddressForm((prev) => ({
                              ...prev,
                              phone: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowHardCopyModal(false);
                          setHardCopyType(null);
                          setAddressForm({
                            name: "",
                            email: "",
                            address: "",
                            city: "",
                            state: "",
                            zipCode: "",
                            country: "",
                            phone: "",
                          });
                        }}
                        className="flex-1 px-4 py-2 border rounded-md hover:bg-muted"
                        disabled={isSubmittingHardCopy}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium disabled:opacity-50"
                        disabled={isSubmittingHardCopy}
                      >
                        {isSubmittingHardCopy ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="animate-spin">⌛</span>
                            Processing...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <Check className="h-4 w-4" />
                            Add to Cart
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryDetailPage;
